import { ErrorState } from "$components/error-state";
import { LoadingLogo } from "$components/loading/loading-logo";
import {
  type Price,
  RXDB_PRICE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/price";
import {
  type PriceListFacility,
  RXDB_PRICE_LIST_FACILITY_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/price-list-facility";
import {
  RXDB_PRICE_LIST_ITEM_COLLECTION_NAME,
  type PriceListItem as RxPriceListItem,
} from "$global-providers/rxdb-provider/collections/price-list-item";
import {
  type PriceListItemExternalReference,
  RXDB_PRICE_LIST_ITEM_EXTERNAL_REFERENCE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/price-list-item-external-reference";
import {
  type PriceListItemName,
  RXDB_PRICE_LIST_ITEM_NAME_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/price-list-item-name";
import {
  type PriceListRevenueDepartment,
  RXDB_PRICE_LIST_REVENUE_DEPARTMENT_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/price-list-revenue-department";
import {
  type PriceListTariff,
  RXDB_PRICE_LIST_TARIFF_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/price-list-tariff";
import { useUser } from "$global-providers/user-provider";
import { Box, Grid } from "@material-ui/core";
import { PriceableItemLanguage, PriceableItemOrigin } from "elephant-web/gql";
import { useMemo } from "react";
import { useRxData } from "rxdb-hooks";
import { useInsuranceProvidersQuery } from "./__generated__/TabContentOnline.gql";
import { InvoiceCard } from "./components";
import EmptySearchState from "./components/EmptySearchState";
import { useFetchPatientInvoices } from "./components/hooks/useFetchPatientInvoices";
import useFilteredInvoices from "./components/hooks/useFilteredInvoices";
import type {
  Payers,
  PriceList,
  PriceListItemRow,
} from "./components/InvoiceCard";
import { InvoiceFilters } from "./components/InvoiceFilters";
import InvoicingEmptyState from "./components/InvoicingEmptyState";
import { capitaliseDependentsEnum } from "./components/insurers";

// Locale hardcoded to English while i18n is not yet wired through the
// price-list read path. Track follow-up when locale support is added.
const PRICE_LIST_LOCALE = PriceableItemLanguage.EnGb;

export const buildPriceList = (
  prices: Price[],
  ownPriceListId: string | null,
  items: RxPriceListItem[],
  names: PriceListItemName[],
  externalRefs: PriceListItemExternalReference[],
  tariffs: PriceListTariff[],
  revenueDepartments: PriceListRevenueDepartment[],
): PriceListItemRow[] => {
  // OWN-wins precedence: for each (itemId, tariffId) keep the row whose
  // priceListId === ownPriceListId, otherwise fall back to whatever else
  // is in scope (the FALLBACK / linked-organisation list).
  const winningPricesByKey = new Map<string, Price>();
  for (const price of prices) {
    const key = `${price.itemId}::${price.tariffId}`;
    const existing = winningPricesByKey.get(key);
    if (!existing) {
      winningPricesByKey.set(key, price);
      continue;
    }
    if (
      price.priceListId === ownPriceListId &&
      existing.priceListId !== ownPriceListId
    ) {
      winningPricesByKey.set(key, price);
    }
  }

  const pricesByItemId = new Map<string, Price[]>();
  for (const price of winningPricesByKey.values()) {
    const bucket = pricesByItemId.get(price.itemId) ?? [];
    bucket.push(price);
    pricesByItemId.set(price.itemId, bucket);
  }

  const tariffById = new Map(tariffs.map((t) => [t.id, t]));
  const revenueDeptById = new Map(revenueDepartments.map((r) => [r.id, r]));
  const externalRefByItemId = new Map(externalRefs.map((e) => [e.itemId, e]));
  const nameByItemId = new Map(names.map((n) => [n.itemId, n]));
  const itemById = new Map(items.map((i) => [i.id, i]));

  const result: PriceListItemRow[] = [];
  for (const [itemId, itemPrices] of pricesByItemId) {
    const item = itemById.get(itemId);
    if (!item) {
      // Item doc not (yet) replicated — skip; the row appears once the
      // item lands. (Active/inactive filtering happens server-side on the
      // online branch too, so we mirror that and don't filter here.)
      continue;
    }
    const nameDoc = nameByItemId.get(itemId);
    if (!nameDoc) {
      // Eventual-consistency placeholder: the item's localised name hasn't
      // replicated yet. Skip the row entirely so the user doesn't see a
      // blank line item; it appears as soon as the name doc lands.
      continue;
    }

    const externalRef = externalRefByItemId.get(itemId);
    if (!externalRef) {
      // Eventual-consistency: the item's external-reference doc hasn't
      // replicated yet. Line-item submission needs
      // priceListItem.externalReference.origin/foreignId to identify the item
      // to the mutation (transformLineItems recovers it from the price list
      // when a row lacks it, but a freshly-selected item has no catalogue
      // fallback here), so surfacing it would produce an unsubmittable row.
      // Hide it until the doc lands.
      continue;
    }

    const payers: Payers[] = itemPrices.map((p) => ({
      tariffId: p.tariffId,
      name: tariffById.get(p.tariffId)?.name,
      amountInPence: p.value,
      revenueDepartment: revenueDeptById.get(p.revenueDepartmentId)?.name,
      revenueDepartmentId: p.revenueDepartmentId,
    }));

    const row: PriceListItemRow = {
      uuid: item.id,
      name: nameDoc.name,
      payers,
      externalReference: {
        // Origin round-trips through the RxDB schema as a generic string;
        // narrow back to the enum at this boundary.
        origin: externalRef.origin as PriceableItemOrigin,
        foreignId: externalRef.foreignId,
      },
    };
    result.push(row);
  }
  return result;
};

export const useLocalPriceList = (
  facilityId: string,
): { loading: boolean; priceList: PriceList } => {
  const { result: facilityRows, isFetching: facilityFetching } =
    useRxData<PriceListFacility>(
      RXDB_PRICE_LIST_FACILITY_COLLECTION_NAME,
      (collection) =>
        collection.findOne({ selector: { id: { $eq: facilityId } } }),
    );

  const facilityRow = facilityRows[0];
  const ownPriceListId = facilityRow?.priceListId ?? null;
  const fallbackPriceListId =
    facilityRow?.linkedOrganisationPriceListId ?? null;
  const scopeIds = useMemo(
    () => [ownPriceListId, fallbackPriceListId].filter(Boolean) as string[],
    [ownPriceListId, fallbackPriceListId],
  );

  const hasScope = scopeIds.length > 0;

  const { result: prices, isFetching: pricesFetching } = useRxData<Price>(
    RXDB_PRICE_COLLECTION_NAME,
    hasScope
      ? (collection) =>
          collection.find({ selector: { priceListId: { $in: scopeIds } } })
      : undefined,
  );

  const itemIds = useMemo(
    () => Array.from(new Set(prices.map((p) => p.itemId))),
    [prices],
  );
  const tariffIds = useMemo(
    () => Array.from(new Set(prices.map((p) => p.tariffId))),
    [prices],
  );
  const revenueDepartmentIds = useMemo(
    () => Array.from(new Set(prices.map((p) => p.revenueDepartmentId))),
    [prices],
  );

  const { result: items } = useRxData<RxPriceListItem>(
    RXDB_PRICE_LIST_ITEM_COLLECTION_NAME,
    itemIds.length > 0
      ? (collection) => collection.find({ selector: { id: { $in: itemIds } } })
      : undefined,
  );

  const { result: names } = useRxData<PriceListItemName>(
    RXDB_PRICE_LIST_ITEM_NAME_COLLECTION_NAME,
    itemIds.length > 0
      ? (collection) =>
          collection.find({
            selector: {
              itemId: { $in: itemIds },
              language: { $eq: PRICE_LIST_LOCALE },
            },
          })
      : undefined,
  );

  const { result: externalRefs } = useRxData<PriceListItemExternalReference>(
    RXDB_PRICE_LIST_ITEM_EXTERNAL_REFERENCE_COLLECTION_NAME,
    itemIds.length > 0
      ? (collection) =>
          collection.find({ selector: { itemId: { $in: itemIds } } })
      : undefined,
  );

  const { result: tariffs } = useRxData<PriceListTariff>(
    RXDB_PRICE_LIST_TARIFF_COLLECTION_NAME,
    tariffIds.length > 0
      ? (collection) =>
          collection.find({ selector: { id: { $in: tariffIds } } })
      : undefined,
  );

  const { result: revenueDepartments } = useRxData<PriceListRevenueDepartment>(
    RXDB_PRICE_LIST_REVENUE_DEPARTMENT_COLLECTION_NAME,
    revenueDepartmentIds.length > 0
      ? (collection) =>
          collection.find({
            selector: { id: { $in: revenueDepartmentIds } },
          })
      : undefined,
  );

  const priceListItems = useMemo(
    () =>
      buildPriceList(
        prices,
        ownPriceListId,
        items,
        names,
        externalRefs,
        tariffs,
        revenueDepartments,
      ),
    [
      prices,
      ownPriceListId,
      items,
      names,
      externalRefs,
      tariffs,
      revenueDepartments,
    ],
  );

  // The price-list is "loading" only while we don't yet know the user's
  // scoped price lists (facility row) or their prices. Items/names/tariffs
  // arriving later fall under the eventual-consistency window and don't
  // block the UI — those rows simply gain detail as docs replicate in.
  const loading = facilityFetching || (hasScope && pricesFetching);

  return {
    loading,
    priceList: { priceListItems },
  };
};

export const TabContentRxdb: FC<{ patientId: string }> = ({ patientId }) => {
  const { facilityId } = useUser();

  const { priceList } = useLocalPriceList(facilityId);

  // Insurers list is still GraphQL but stays usable offline: cache-first
  // returns the cached list and errorPolicy: 'ignore' suppresses the toast
  // in airplane mode (per PNE-357 AC — finalisation must work offline).
  const { data: insurersData, loading: insurersLoading } =
    useInsuranceProvidersQuery({
      fetchPolicy: "cache-first",
      errorPolicy: "ignore",
    });
  const { invoices, loading, error } = useFetchPatientInvoices(patientId);
  const { filteredInvoices, ...invoiceFilters } = useFilteredInvoices(invoices);

  if ((loading && invoices.length === 0) || insurersLoading)
    return <LoadingLogo />;

  if (error)
    return (
      <Box p={3}>
        <ErrorState variant="inline" error={error} />
      </Box>
    );

  if (!loading && invoices.length === 0) {
    return <InvoicingEmptyState />;
  }

  const insurers = capitaliseDependentsEnum(
    insurersData?.minInsuranceProviders ?? [],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <InvoiceFilters {...invoiceFilters} />
      </Grid>
      {filteredInvoices.length < 1 ? (
        <EmptySearchState />
      ) : (
        filteredInvoices?.map((invoice) => (
          <Grid item xs={12} key={invoice.invoiceId}>
            <InvoiceCard
              {...invoice}
              priceList={priceList}
              // @ts-expect-error Unfortunate cast as api-insurance returns a JsonObject for the properties field
              insurers={insurers}
              balance={invoice.invoiceSummary.balance}
            />
          </Grid>
        ))
      )}
    </Grid>
  );
};
